import { ElementRef, Injectable, QueryList, inject } from '@angular/core';
import { LanguageType } from '../../constants/language-eum';
import { AppTextbox, CommonService, DataGrid, DataGridColumnConfig, DataGridComponent, InputType, SKIP_LOADER, SKIP_BEARER_TOKEN, TranslationService } from '@waseel-asp/tpa-waseel-lib-staging';
import { HttpContext, HttpRequest } from '@angular/common/http';
import { GUID_FORMAT_REGEX, NULL_VIEW_SYMBOL, SKIP_ERROR_TOASTY } from '../../constants/system-constants.model';
import { HttpRequestService } from '../http-request/http-request.service';
import { animationFrameScheduler, asyncScheduler, catchError, filter, fromEvent, interval, map, merge, Observable, of, pairwise, startWith, switchMap, take } from 'rxjs';
import { CallbackFunction } from '../../functions/config-function';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { ImportExportDialogComponent } from '../../components/import-export-dialog/import-export-dialog.component';
import { MimeTypeName } from '../../constants/mime-types';
import { ClaimDetailsMap } from '../../components/claim-card/models/claim-card.model';
import { ToastrHelperService } from './toast-helper.service';
import { ButtonHelperService } from './button-helper.service';
import { GRID_PAGE_DEFAULT_SIZE } from '../../constants/grid-constant';
import { gridFeatures } from '../../../core/modules/admin-modules/eligibility/view-eligibility/models/view-eligibility.model';
import { DialogService } from '../dialog/dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { DataGridFilterCondition } from '../../constants/datagrid-filter-condition.enum';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { ApprovalCommunication, ApprovalCommunicationNote, IConvertedCommunication } from '../../components/common-communication/model/communication.model';
import { IApprovalItem } from '../../../core/modules/admin-modules/pre-auth/preauth-request/models/preauth-request.model';
import { EnumService } from './enum.service';
import { UserType, UserTypeLabel } from '../../constants/user-type-enum';
import { DynamicLanguageFieldPipe } from '../../pipes/language-translate.pipe';
import { IPBMResult, IPBMResultDetails, ISettingsPayload } from '../../models/common-models';
import { ApprovalSubType } from '../../constants/preauth-request.enum';
import { PATH_ADV_PREAUTH_TRANS, PATH_CLAIM_TRANS, PATH_PREAUTH_TRANS, PATH_PRESCRIPTION_TRANS, ROUTE_CLAIM, ROUTE_PREAUTH, ROUTE_PRESCRIPTION } from '../../constants/route-consts';
import { SLADetailsMapper, SLAStatus } from '../../components/common-chart/models/common-chart.model';
import { ApprovalType, normalizeApprovalType } from '../../constants/apporval-type-enum';
import { PERMISSION } from '../../constants/permission';

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  private translation = inject(TranslationService);
  private commonService = inject(CommonService);
  private httpRequestService = inject(HttpRequestService);
  private toastrHelperService = inject(ToastrHelperService);
  private buttonHelperService = inject(ButtonHelperService);
  private dialogService = inject(DialogService);
  private dialog = inject(MatDialog);
  private enumService = inject(EnumService);

  cloneRequestWithNewToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
  }

  deleteOption<T>(url?: string, entityName?: string, grid?: DataGridComponent<T>, callback?: CallbackFunction): void {
    this.dialogService.confirmActionDialog()
      .pipe(
        switchMap(result => result ? this.httpRequestService.delete(url) : of(false)),
        catchError(() => of(false))
      ).subscribe((result) => {
        if (result !== false) {
          const entity = entityName ? entityName : this.translation.instant("CONFIGURATION.PRICE_LIST.RECORD");
          this.toastrHelperService.showDeletedSuccessMessage(entity);
          grid && grid?.loadData();
          callback && callback();
        }
      });
  }

  cancelPBMOption<T>(url?: string, entityName?: string, grid?: DataGridComponent<T>, callback?: CallbackFunction): void {
    this.dialogService.confirmActionDialog(this.translation.instant('COMMON.CANCEL_CONFIRMATION_MSG'))
      .pipe(
        switchMap(result => result ? this.httpRequestService.post(url, null) : of(false)),
        catchError(() => of(false))
      ).subscribe((result: IPBMResult | false) => {
        if (result) {
          const entity = entityName ? entityName : this.translation.instant("COMMON.PBM");
          if (result.status == "success")
            this.toastrHelperService.showCustomSuccessMessage(entity, 'PREAUTH.STATUS_TYPE.CANCELLED');
          else if (result?.errors || result?.status.toLowerCase() == 'failed') {
            const errorMsg = this.parsedPBMError(result);
            this.toastrHelperService.showErrorMessage(Array.isArray(errorMsg) ? errorMsg.join(', ') : errorMsg);
            return;
          }
          grid && grid?.loadData();
          callback && callback();
        }
      });
  }

  agreeOption<T>(isAgree?: boolean, url?: string, entityName?: string, grid?: DataGridComponent<T>, callback?: CallbackFunction): void {
    let res;
    this.dialogService.confirmActionDialog(isAgree ? this.translation.instant('COMMON.AGREE_CONFIRMATION_MSG') : this.translation.instant('COMMON.DISAGREE_CONFIRMATION_MSG'))
      .pipe(
        switchMap(result => {
          res = result;
          return result ? this.httpRequestService.put(url) : of(false);
        }),
        catchError(() => of(false))
      ).subscribe(() => {
        if (res !== false) {
          const entity = entityName ? entityName : this.translation.instant("CONFIGURATION.PRICE_LIST.RECORD");
          this.toastrHelperService.showCustomSuccessMessage(entity, isAgree ? 'COMMON.AGREE' : 'COMMON.DISAGREE');
          grid && grid?.loadData();
          callback && callback();
        }
      });
  }

  changePasswordIconAndFieldType(config: AppTextbox) {
    if (config.suffixIcon.icon != 'visibility_off') {
      config.suffixIcon.icon = 'visibility_off';
      config.type = InputType.password;
    } else {
      config.suffixIcon.icon = 'visibility';
      config.type = InputType.text;
    }
    config = { ...config };
  }

  disableFilterControl<T>(config: T, controlName: string, filterFormGroup?: FormGroup) {
    const control = config['features'].filter.filterConfig.formControls.controls.find(x => x.control['formControlName'] === controlName);
    control.control['features'].isDisable = true;
    control.control['selectedOptions'] = [];
    filterFormGroup?.get(controlName).setValue(null);
    control.control = { ...control.control };
  }

  resetFilterControl<T>(config: T, controlName: string, filterFormGroup?: FormGroup) {
    const Control = config['features'].filter.filterConfig.formControls.controls.find(x => x.control['formControlName'] === controlName);
    Control.control['features'].isDisable = false;
    filterFormGroup?.get(controlName).setValue(null);
    Control.control = { ...Control.control };
  }

  openUploadDialog<T>(apiURL?: string, grid?: DataGridComponent<T>, fileType?: string, callback?: CallbackFunction) {
    this.dialog.open(ImportExportDialogComponent, {
      data: {
        title: this.translation.instant('FORM_FIELD.UPLOAD_EXCEL'),
        API: apiURL,
        fileType: fileType || MimeTypeName.xlsx
      },
      panelClass: ['primary-modal', 'size-sm'],
    }).afterClosed().pipe(take(1)).subscribe((res) => {
      if (res) {
        grid && grid.loadData();
        callback && callback(res);
      }
    });
  }

  toSmartTitleCase(str: string): string {
    if (!str) return str;
    return str.split(/([-\s])/g) // split but keep the separators (hyphen and space)
      .map(part => {
        if (part == NULL_VIEW_SYMBOL || part === ' ') return part; // keep separator as-is
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }).join('');
  }

  customPatternValidation(pattern: RegExp, message): ValidatorFn {
    return ({ value }: AbstractControl) => !value || pattern.test(value) ? null : { customPattern: this.translation.instant(message) };
  }

  isArabic() {
    return this.translation.currentLanguage() == LanguageType.Arabic;
  }

  isValidJson(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }

  static filterValidAmounts<T>(object: T) {
    return object && Object.values(object).every(value => value !== 0 && !isNaN(value as number));
  }

  cleanObject<T>(obj: T): T | null {
    if (obj === null || obj === undefined)
      return null;
    if (typeof obj === 'object' && !Array.isArray(obj)) {
      let allFieldsEmpty = true;
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];
          // Recursively process nested objects
          const cleanedValue = this.cleanObject(value);
          (obj[key] as T[keyof T]) = cleanedValue;
          if (cleanedValue !== null && cleanedValue !== '')
            allFieldsEmpty = false;
        }
      }
      return allFieldsEmpty ? null : obj;
    }
    return obj;
  }

  getSkipLoaderContext() {
    return new HttpContext().set(SKIP_LOADER, true);
  }

  getSkipBearerTokenContext() {
    return new HttpContext().set(SKIP_BEARER_TOKEN, true);
  }

  getSkipErrorToastyContext() {
    return new HttpContext().set(SKIP_ERROR_TOASTY, true);
  }

  formatBytes(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
  }

  getPrimaryColor() {
    return getComputedStyle(document.documentElement).getPropertyValue('--primary');
  }

  setActiveCard<T>(element: T, id: number) {
    element['children'][0].children[0].classList.remove('card-active');
    if (element['children'][0].id === id.toString()) {
      element['children'][0].children[0].classList.add('card-active');
      const activeCard = document.querySelector('.card-active') as HTMLElement;
      if (activeCard) {
        activeCard.style.setProperty('--card-bg-color', ClaimDetailsMap[id].backgroundColor);
        activeCard.style.setProperty('--card-border-color', ClaimDetailsMap[id].color);
        activeCard.style.setProperty('--card-box-shadow', `0px 0px 15px ${ClaimDetailsMap[id].backgroundColor}`);
      }
    }
  }

  sumOfArrayElement<T>(values: T[]): number {
    return values.map(value => parseFloat(value as string)) // Ensure each value is a decimal number
      .filter(value => !isNaN(value))  // Remove NaNs from parsing errors
      .reduce((sum, val) => sum + val, 0); // Sum the array
  }

  generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getButtonIdWithoutGuid(buttonId: string): string {
    if (!buttonId) return buttonId;
    const guidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const match = buttonId.match(guidRegex);
    if (!match) return buttonId;
    return buttonId.slice(0, -match[0].length);
  }

  toCamelCase(label: string): string {
    return label
      .toLowerCase()
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase())
      .replace(/\s+/g, '');
  }

  scrollToBottom(listOrElement: QueryList<ElementRef> | ElementRef) {
    const htlmElement = this.getLastHtmlElement(listOrElement);
    if (htlmElement) {
      const rect = htlmElement.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
      if (!isVisible)
        htlmElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  scrollToTop(element: QueryList<ElementRef> | ElementRef) {
    const htlmElement = this.getLastHtmlElement(element);
    if (htlmElement) {
      asyncScheduler.schedule(() => {
        let headerHeight = 100;
        const headerEl = document.querySelector('.header') as HTMLElement;
        if (headerEl)
          headerHeight = headerEl.offsetHeight + 10;

        const elementPosition = htlmElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerHeight;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }, 0);
    }
  }

  manualChangeTriggered() {
    this.commonService.isManualChangeTriggered.next(true);
  }

  async scrollToInvalidController(elementRef: ElementRef | HTMLElement | ElementRef[], isFormArray: boolean = false): Promise<boolean> {
    if (isFormArray && Array.isArray(elementRef)) {
      for (const formRef of elementRef) {
        const root = formRef.nativeElement as HTMLElement;
        const invalid = root.querySelector('.ng-invalid:not(form):not(fieldset)');
        if (invalid) {
          this.scrollToInvalidController(formRef, false);
          return true;
        }
      }
      return null;
    }

    const findDeepestInvalidElement = (root: HTMLElement): HTMLElement | null => {
      const invalidElements = root.querySelectorAll('.ng-invalid:not(form):not(fieldset)');
      for (const element of Array.from(invalidElements)) {
        const innerInvalid = element.querySelector('.ng-invalid:not(form):not(fieldset)');
        if (!innerInvalid) return element as HTMLElement;
      }
      return null;
    };

    const rootElement = elementRef instanceof ElementRef ? elementRef.nativeElement : (elementRef as HTMLElement);
    const firstInvalidControl = findDeepestInvalidElement(rootElement);
    if (!firstInvalidControl) return null;

    await this.ensureParentAccordionsOpen(firstInvalidControl);
    await this.scrollAndFocus(firstInvalidControl);
    return true;
  }

  private async scrollAndFocus(target: HTMLElement): Promise<void> {
    const header = document.querySelector('.header') as HTMLElement;
    const headerOffset = header ? header.offsetHeight : 0;
    const elementRect = target.getBoundingClientRect();
    if (elementRect.top < headerOffset)
      target.style.scrollMarginTop = `${headerOffset + 24}px`;

    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animationFrameScheduler.schedule(() => {
              target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            });

            const formField = target.closest('mat-form-field');
            const dateToggleBtn = formField ? formField.querySelector('mat-datepicker-toggle button') as HTMLElement : null;
            const matSelectTrigger = target.querySelector('.mat-mdc-select-trigger');
            const radioInput = target.querySelector('mat-radio-button input[type="radio"]') as HTMLInputElement;
            const checkBoxInput = target.querySelector('mat-checkbox input[type="checkbox"]') as HTMLInputElement;
            if (matSelectTrigger) {
              target.focus();
              (matSelectTrigger as HTMLElement).click();
            } else if (radioInput)
              radioInput.focus();
            else if (checkBoxInput)
              checkBoxInput.focus();
            else if (dateToggleBtn) {
              target.focus();
              dateToggleBtn.click();
            } else
              target.focus();

            observer.disconnect();
            resolve();
          }
        });
      }, { threshold: [0.5, 0.75, 1.0] });
      observer.observe(target);
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  async waitForTabAnimation(tabGroup: HTMLElement): Promise<void> {
    const activeBody = tabGroup.querySelector('.mat-mdc-tab-body-active');
    if (!activeBody) return;

    return new Promise((resolve) => {
      const onTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
          activeBody.removeEventListener('transitionend', onTransitionEnd);
          resolve();
        }
      };
      activeBody.addEventListener('transitionend', onTransitionEnd);
      setTimeout(resolve, 600);
    });
  }

  async ensureParentAccordionsOpen(element: HTMLElement): Promise<void> {
    let parent = element.parentElement;
    const openingPromises: Promise<void>[] = [];

    while (parent) {
      if (parent.tagName.toLowerCase() === 'mat-expansion-panel') {
        const isExpanded = parent.classList.contains('mat-expanded');

        if (!isExpanded) {
          const header = parent.querySelector('mat-expansion-panel-header') as HTMLElement;
          const contentArea = parent.querySelector('.mat-expansion-panel-content') as HTMLElement;

          if (header && contentArea) {
            const animationFinished = new Promise<void>((resolve) => {
              const onTransitionEnd = (event: TransitionEvent) => {
                if (event.propertyName === 'height' || event.propertyName === 'grid-template-rows') {
                  contentArea.removeEventListener('transitionend', onTransitionEnd);
                  resolve();
                }
              };
              contentArea.addEventListener('transitionend', onTransitionEnd);
              setTimeout(resolve, 600);
            });
            header.click();
            openingPromises.push(animationFinished);
          }
        }
      }
      parent = parent.parentElement;
    }

    if (openingPromises.length > 0) {
      await Promise.all(openingPromises);
    }
  }

  getJsonGridConfig<T>(columns: DataGridColumnConfig<T>[], gridData: T[], viewButtonCallBack: (data?: T) => void, downloadButtonCallback: (data?: T) => void): DataGrid<T> {
    const config: DataGrid<T> = {
      primaryKey: columns[0].formControlName as keyof T,
      actionButtons: [
        {
          ...this.buttonHelperService.getViewButtonConfig(),
          callback: (data?: T) => viewButtonCallBack(data)
        },
        {
          ...this.buttonHelperService.getDownloadButtonConfig(false),
          callback: (data?: T) => downloadButtonCallback(data)
        }
      ],
      columns: columns,
      features: {
        ...gridFeatures,
        setTimeout: true
      },
      gridData: {
        data: gridData
      },
      id: 'jsonGrid'
    };
    return config;
  }

  getPageSizeFromGrid<T>(grid: DataGridComponent<T>): number {
    if (grid?.paginator?.pageSize)
      return grid.paginator.pageSize;
    if (grid?.pageSizeOptionsArray?.length)
      return grid.pageSizeOptionsArray[0];
    return GRID_PAGE_DEFAULT_SIZE;
  }

  scrollReachAtBottom(threshold: number = window.outerWidth <= 350 ? 170 : 50) {
    const panel = document.scrollingElement || document.documentElement;
    return panel.scrollTop + panel.clientHeight >= panel.scrollHeight - threshold;
  }

  getScrollReachBottomWhenScrollingDown$(hasMoreData: () => boolean, threshold?: number): Observable<Event | null> {
    return merge(fromEvent(window, 'scroll'), interval(300))
      .pipe(
        map((event) => {
          const panel = document.scrollingElement || document.documentElement;
          return {
            event: event as Event | null,
            scrollTop: panel.scrollTop,
            isScrollable: panel.scrollHeight > panel.clientHeight
          };
        }),
        startWith({
          event: null,
          scrollTop: document.scrollingElement?.scrollTop ?? window.scrollY ?? 0,
          isScrollable: (document.scrollingElement?.scrollHeight ?? 0) > (document.scrollingElement?.clientHeight ?? 0)
        }),
        pairwise(),
        filter(([previous, current]) => {
          if (this.dialog.openDialogs.length > 0) return false;
          if (!current.isScrollable)
            return hasMoreData() && this.scrollReachAtBottom(threshold);
          return (current.scrollTop > previous.scrollTop && hasMoreData() && this.scrollReachAtBottom(threshold));
        }),
        map(([, current]) => current.event)
      );
  }

  getUrlWithoutGUID(url: string | null): string | null {
    if (!url) return null;
    const sanitizedUrl = url.split('?')[0].split('#')[0];
    return sanitizedUrl.replace(GUID_FORMAT_REGEX, '/id');
  }

  calculateFactorfromDiscountPercentage(discountPercentage: number): number {
    return parseFloat((1 - (discountPercentage / 100)).toFixed(2));
  }

  parsedPBMError(response: IPBMResult | { errors?: { description?: string }[]; responseJson?: string; results?: IPBMResultDetails; status?: string }): string | string[] {
    if (response?.responseJson) {
      if (this.isValidJson(response.responseJson)) {
        const parsed = JSON.parse(response.responseJson);
        if (Array.isArray(parsed?.errorDescriptions))
          return parsed.errorDescriptions;
        if (parsed?.error)
          return parsed.error;
      }
    }

    if (response?.results) {
      if (Array.isArray(response.results?.errorDescriptions))
        return response.results?.errorDescriptions;

      if (response?.results?.errors) {
        if (Array.isArray(response.results.errors))
          return response.results.errors;

        if (typeof response?.results?.errors === 'object') {
          if (Array.isArray(response.results.errors?.errorDescriptions))
            return response.results.errors?.errorDescriptions;
          if (typeof response.results.errors?.error === 'string')
            return response.results.errors.error;
        }
      }

      if (typeof response?.results?.errors === 'string')
        return response.results.errors;
    }

    if (Array.isArray(response?.errors)) {
      if (response.errors?.every(e => typeof e === 'string'))
        return response.errors as string[];

      for (const err of response.errors) {
        const errObj = err as { description?: string };
        if (typeof errObj?.description === 'string') {
          if (this.isValidJson(errObj.description)) {
            const descParsed = JSON.parse(errObj.description);
            if (Array.isArray(descParsed?.errorDescriptions))
              return descParsed.errorDescriptions;
            if (descParsed?.error)
              return descParsed.error;
          } else
            return errObj.description;
        }
      }
      return response.errors as string[];
    }

    if (typeof response?.errors === 'string')
      return response.errors;

    return this.translation.instant('COMMON.SOMETHING_WENT_WRONG');
  }

  private getLastHtmlElement(element: QueryList<ElementRef> | ElementRef): HTMLElement | undefined {
    if (!element) return undefined;
    let nativeElement = null;
    if (element instanceof QueryList)
      nativeElement = element.length > 0 ? element.last : undefined;
    else
      nativeElement = element;
    return nativeElement ? nativeElement.nativeElement as HTMLElement : undefined;
  }

  createSettingsPayload(columnName: string, filterValue?: string): ISettingsPayload {
    const commonPayload = {
      columns: [{
        name: columnName,
        searchable: true,
        orderable: true,
        filterable: filterValue ? true : false,
        filterSearch: filterValue ? { value: filterValue, condition: DataGridFilterCondition.IsEqualTo } : null,
        excelColumnName: columnName
      }],
      defaultSortingColumn: columnName,
      pageNumber: 0,
      pageSize: 0,
      sortBy: columnName,
      sortOrder: 'asc',
      generalSearch: null,
      isSearchByCriteria: true
    };
    return commonPayload;
  }

  updateCountryCityStateFormState(formGroup: FormGroup, group: string) {
    const countryIdControl = formGroup.get(`${group}.countryId`);
    const stateIdControl = formGroup.get(`${group}.stateId`);
    const cityIdControl = formGroup.get(`${group}.cityId`);

    if (!countryIdControl.value) {
      stateIdControl.disable();
      cityIdControl.disable();
    } else {
      stateIdControl.enable();
      cityIdControl.enable();
    }

    if (!stateIdControl.value)
      cityIdControl.disable();
    else
      cityIdControl.enable();
  }

  resetDates(controlNames: string[], actions: Array<'reset' | 'enable' | 'disable'>, formGroup?: FormGroup): void {
    controlNames.forEach(controlName => {
      const control = formGroup.get(controlName);
      actions.forEach(action => {
        control?.[action]();
      });
    });
  }

  renameKeysDeep<T>(data: T, keyMap: Record<string, string>, reverse: boolean = false): T {
    const effectiveMap = reverse ? Object.fromEntries(Object.entries(keyMap).map(([k, v]) => [v, k])) : keyMap;
    if (Array.isArray(data))
      return data.map(item => this.renameKeysDeep(item, keyMap, reverse)) as unknown as T;
    if (data !== null && typeof data === 'object') {
      return Object.keys(data).reduce((acc: Record<string, unknown>, key) => {
        const newKey = effectiveMap[key] ?? key;
        acc[newKey] = this.renameKeysDeep((data as Record<string, unknown>)[key], keyMap, reverse);
        return acc;
      }, {}) as T;
    }
    return data;
  }

  downloadFile(file: File) {
    const blob = new Blob([file], { type: file.type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  getSlidebarConfig = (): OwlOptions => {
    const navIcons = this.isArabic()
      ? ['<span class="material-icons">chevron_right</span>', '<span class="material-icons">chevron_left</span>']
      : ['<span class="material-icons">chevron_left</span>', '<span class="material-icons">chevron_right</span>'];

    return {
      mouseDrag: true,
      touchDrag: false,
      pullDrag: false,
      dots: false,
      autoHeight: true,
      autoWidth: true,
      items: 4,
      loop: false,
      slideBy: 1,
      navText: navIcons,
      rtl: this.isArabic(),
      responsive: {
        0: { items: 1 },
        400: { items: 2 },
        740: { items: 3 },
        940: { items: 4 },
        1080: { items: 4 }
      },
      nav: true
    };
  };

  getCurrentLocale(): string {
    return this.isArabic() ? 'ar-EG' : 'en-US';
  }

  getGreedRedIndicator(isGreen: boolean | string): string {
    return `<span style="display:flex;align-items:center;gap:8px;">
              <span style="
                width:14px;
                height:14px;
                border-radius:50%;
                display:inline-block;
                background: radial-gradient(circle at 30% 30%, ${isGreen ? '#7df7a3' : '#ff8a8a'}, ${isGreen ? '#2e7d32' : '#c62828'});
                box-shadow:
                  inset 0 1px 2px rgba(255,255,255,0.6),
                  inset 0 -1px 2px rgba(0,0,0,0.4),
                  0 1px 2px rgba(0,0,0,0.3);
              "></span>
            </span>`;
  }

  getCreatedByName(createdBy: string, addedByUsertype: number, userIdMap: any): string {
    const userMap = userIdMap.get(createdBy);
    const addedByUserTypeLabel = this.enumService.getLabelByMappedValue(UserType, UserTypeLabel, addedByUsertype);
    return userMap ? (userMap.userType != UserType.TPA ? `${userMap.userType} - ${userMap.userFullName}` : `${userMap.userType}(${addedByUserTypeLabel}) - ${userMap.userFullName}`) : '';
  }

  getItemWithDescription(approvalItem: IApprovalItem) {
    const dynamicLanguageField = new DynamicLanguageFieldPipe();
    return `[Item ${approvalItem.seqNo}] - ${dynamicLanguageField.transform(approvalItem.serviceCode, 'serviceTypeName')} - ${approvalItem.serviceCode?.code} - ${dynamicLanguageField.transform(approvalItem.serviceCode, 'description')}`;
  }

  convertCommunicationNote(note: ApprovalCommunicationNote, userIdMap: any) {
    const createdBy = userIdMap.get(note.createdBy) || NULL_VIEW_SYMBOL;
    return {
      approvalCommunicationId: note.approvalCommunicationId,
      approvalCommunicationNoteId: note.approvalCommunicationNoteId,
      note: note.note,
      createdBy: createdBy ? `${createdBy.userType} - ${createdBy.userFullName}` : NULL_VIEW_SYMBOL,
      createdDate: note.createdDate
    };
  }

  convertCommunicationResponse(response: ApprovalCommunication[], userIdMap: any) {
    const convertedResponse: IConvertedCommunication = {
      approvalCommunicationList: [],
      allCommunicationNotes: [],
      visibleCommunication: []
    };
    const dynamicLanguageField = new DynamicLanguageFieldPipe();
    response && response.forEach(element => {
      if (element.replyOfApprovalCommunicationId) return;
      const communication = {
        approvalCommunicationId: element.approvalCommunicationId,
        communicationId: element.communicationId,
        approvalRequestId: element.approvalRequestId,
        approvalType: element.approvalType,
        isRead: element.isRead,
        createdDate: element.createdDate,
        createdBy: element.createdBy,
        createdByName: this.getCreatedByName(element.createdBy, element.addedByUsertype, userIdMap),
        isReadjudication: element.isReadjudication,
        approvalCommunicationDetails: [],
        approvalCommunicationResponse: [],
        approvalCommunicationNotes: [],
        userType: element.userType,
        addedByUsertype: element.addedByUsertype,
        reason: dynamicLanguageField.transform(element, 'reasonCodeName'),
        category: dynamicLanguageField.transform(element, 'categoryTypeName')
      };
      element.approvalCommunicationDetails.forEach(elem => {
        communication.approvalCommunicationDetails.push({
          approvalCommunicationId: elem.approvalCommunicationId,
          approvalCommunicationDetailId: elem.approvalCommunicationDetailId,
          itemDetails: elem.approvalItem && elem.approvalItem.serviceCode ? this.getItemWithDescription(elem.approvalItem) : null,
          message: elem.message,
          approvalItemId: elem.approvalItem ? elem.approvalItem.approvalItemId : null,
          attachmentName: elem.attachmentName,
          attachmentType: elem.attachmentType,
          createdBy: elem.createdBy,
          createdByName: this.getCreatedByName(elem.createdBy, element.addedByUsertype, userIdMap),
          createdDate: elem.createdDate
        });
      });

      element.approvalCommunicationNotes?.forEach(note => {
        const notes = this.convertCommunicationNote(note, userIdMap);
        communication.approvalCommunicationNotes.push(notes);
        convertedResponse.allCommunicationNotes.push(notes);
      });

      const res = response.filter(item => item.replyOfApprovalCommunicationId === element.approvalCommunicationId);
      res && res.forEach(element => {
        const approvalCommunicationDetails = {
          communicationId: element.communicationId,
          responseGroup: [],
          createdDate: null,
          createdBy: null,
          createdByName: null
        };
        element.approvalCommunicationDetails && element.approvalCommunicationDetails.forEach(elem => {
          approvalCommunicationDetails.createdDate = elem.createdDate;
          approvalCommunicationDetails.createdBy = elem.createdBy;
          approvalCommunicationDetails.createdByName = this.getCreatedByName(elem.createdBy, element.addedByUsertype, userIdMap);
          convertedResponse.visibleCommunication.push({
            approvalCommunicationId: elem.approvalCommunicationId,
            isRead: element.isRead,
            userType: element.userType,
            addedByUsertype: element.addedByUsertype
          });
          approvalCommunicationDetails.responseGroup.push({
            approvalCommunicationId: elem.approvalCommunicationId,
            approvalCommunicationDetailId: elem.approvalCommunicationDetailId,
            itemDetails: elem.approvalItem && elem.approvalItem.serviceCode ? this.getItemWithDescription(elem.approvalItem) : null,
            approvalItemId: elem.approvalItem ? elem.approvalItem.approvalItemId : null,
            message: elem.message,
            isRead: element.isRead,
            attachmentName: elem.attachmentName,
            attachmentType: elem.attachmentType,
            communicationId: element.communicationId,
            createdBy: elem.createdBy,
            createdByName: this.getCreatedByName(elem.createdBy, element.addedByUsertype, userIdMap),
            createdDate: elem.createdDate
          });
        });
        communication.approvalCommunicationResponse.push(approvalCommunicationDetails);
      });
      convertedResponse.visibleCommunication.push({
        approvalCommunicationId: communication.approvalCommunicationId,
        isRead: communication.isRead,
        userType: communication.userType,
        addedByUsertype: communication.addedByUsertype
      });
      convertedResponse.approvalCommunicationList.push(communication);
    });
    return convertedResponse;
  }

  getRouteToViewPage(screenType: ApprovalSubType) {
    switch (screenType) {
      case ApprovalSubType.Preauth:
        return PATH_PREAUTH_TRANS;
      case ApprovalSubType.AdvancePreauth:
        return PATH_ADV_PREAUTH_TRANS;
      case ApprovalSubType.Claim:
        return PATH_CLAIM_TRANS;
      case ApprovalSubType.Prescription:
        return PATH_PRESCRIPTION_TRANS;
    }
  }

  getRouteToMyApprovalPage(screenType: ApprovalSubType) {
    switch (screenType) {
      case ApprovalSubType.Preauth:
        return ROUTE_PREAUTH;
      case ApprovalSubType.Claim:
        return ROUTE_CLAIM;
      case ApprovalSubType.Prescription:
        return ROUTE_PRESCRIPTION;
      default:
        return null;
    }
  }

  generatePayloadFromId(gridId: string, defaultColumn: string = '', localStorageKey: string = 'gridConfigs'): any {
    const storedString = localStorage.getItem(localStorageKey);

    if (!storedString) {
      return null;
    }
    const storedData: any[] = JSON.parse(storedString);
    const gridConfig = storedData.find(item => item.id === gridId);

    if (!gridConfig || !gridConfig.data) {
      return null;
    }

    const data = gridConfig.data;

    const columns = (data.filterData || []).map((filter: any) => {
      let filterSearchObj = null;

      if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
        filterSearchObj = {
          value: filter.value.toString(),
          condition: DataGridFilterCondition.IsEqualTo,
          checkDateInUTCRange: true
        };
      }

      return {
        name: filter.formControlName,
        searchable: true,
        orderable: true,
        filterable: true,
        filterSearch: filterSearchObj,
        excelColumnName: filter.label
      };
    });

    if (defaultColumn) {
      columns.push({
        name: defaultColumn,
        searchable: true,
        orderable: true,
        filterable: true,
        filterSearch: null,
        excelColumnName: defaultColumn
      });
    }

    const payload = {
      columns: columns,
      defaultSortingColumn: data.sortActive || "",
      pageNumber: data.pageNo || 0,
      pageSize: data.pageSize || 0,
      sortBy: data.sortActive || "",
      sortOrder: data.sortDirection || "asc",
      generalSearch: data.currentSearch || "",
      isSearchByCriteria: true
    };

    return payload;
  }

  closeDialog(isAllClose: boolean = false) {
    if (isAllClose) {
      this.dialog.closeAll();
    } else {
      const openedDialogs = this.dialog.openDialogs;
      if (openedDialogs.length > 0) {
        const topDialog = openedDialogs[openedDialogs.length - 1];
        topDialog.close();
      }
    }
  }

  getTickUnTickIndicator(isTick: boolean, isActive: boolean): string {
    const color = isActive ? '#1ea13f' : '#6c757d';
    return isTick ? `<span class="material-icons" style="color: ${color};">done</span>` : '';
  }

  getStatusLegendTemplate(): string {
    return this.buildLegendWrapper(this.buildStatusLegendItems());
  }

  private buildLegendWrapper(content: string): string {
    return `
      <div style="display:flex;flex-wrap:wrap;align-items:center;gap:16px;font-size:13px;color:#555;width:100%;">
        ${content}
      </div>
    `;
  }

  private buildStatusLegendItems(): string {
    return this.getStatusLegendConfig().map(item => this.getLegendItem(item.color, item.label, item.isBordered)).join('');
  }

  private getStatusLegendConfig(): { color: string; label: string, isBordered?: boolean }[] {
    return [
      { color: SLADetailsMapper[SLAStatus.Breached].color, label: this.translation.instant(SLADetailsMapper[SLAStatus.Breached].title) },
      { color: SLADetailsMapper[SLAStatus.CompletedWithinSLA].color, label: this.translation.instant(SLADetailsMapper[SLAStatus.CompletedWithinSLA].title) },
      { color: SLADetailsMapper[SLAStatus.NearBreach].color, label: this.translation.instant(SLADetailsMapper[SLAStatus.NearBreach].title) },
      { color: '#fffae7', label: this.translation.instant('COMMON.HIGH_COST_ESCALATION'), isBordered: true }
    ];
  }

  private getLegendItem(color: string, label: string, isBordered = false): string {
    const swatchStyle = isBordered
      ? `width:1rem;height:14px;background:${color};display:inline-block;border-radius:3px;border:1px solid #D97706;`
      : `width:1rem;height:6px;background:${color};display:inline-block;border-radius:2px;`;

    return `
      <div style="display:flex; align-items:center; gap:6px;">
        <span style="${swatchStyle}"></span>
        <span>${label}</span>
      </div>
    `;
  }

  getScreenFromApprovalType(approvalType: ApprovalType | string | number | null | undefined): string {
    switch (normalizeApprovalType(approvalType)) {
      case ApprovalType.Claim:
        return PERMISSION.CLAIM.NAME;
      case ApprovalType.Prescription:
        return PERMISSION.PRESCRIPTION.NAME;
      case ApprovalType.Preauth:
      default:
        return PERMISSION.PRE_AUTH.NAME;
    }
  }

  getScreenFromProcessFlags(isClaim: boolean, isPrescription: boolean): string {
    if (isPrescription)
      return PERMISSION.PRESCRIPTION.NAME;
    else if (isClaim)
      return PERMISSION.CLAIM.NAME;
    else
      return PERMISSION.PRE_AUTH.NAME;
  }

}
